import { useCallback, useMemo, useRef } from "react";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import {
    collectItemValues,
    mergeValueChange,
} from "@components/pageHeader/filters/FilterPickerList/filterPickerTree";
import { BaseFilterProps } from "../../lib";
import styles from "../CategoryFilter/AggregatedCategoryFilter.module.scss";
import {
    CoaTemplateGroupOption,
    getSelectedParentGroupKeys,
    toCoaTemplateGroupItem,
} from "./CoaTemplateGroupsFilter.lib";

interface Props extends BaseFilterProps {
    options: CoaTemplateGroupOption[];
}

export const CoaTemplateGroupsFilter = ({
    filters,
    onChange,
    options,
}: Props) => {
    const selectedKeys = useMemo(
        () => filters.coaTemplateGroupKeys ?? [],
        [filters.coaTemplateGroupKeys],
    );
    const keysSelectedOnOpen = useRef(selectedKeys);
    const items = useMemo(() => options.map(toCoaTemplateGroupItem), [options]);
    const itemKeys = useMemo(() => new Set(collectItemValues(items)), [items]);
    const expandedParentKeys = useMemo(
        () => getSelectedParentGroupKeys(options, keysSelectedOnOpen.current),
        [options],
    );

    const handleValueChange = useCallback(
        (next: string[]) => {
            const updatedKeys = mergeValueChange({
                previous: selectedKeys,
                next,
                knownValues: itemKeys,
            });
            onChange({
                coaTemplateGroupKeys:
                    updatedKeys.length > 0 ? updatedKeys : undefined,
            });
        },
        [itemKeys, onChange, selectedKeys],
    );

    return (
        <div className={styles.filter}>
            <FilterPickerList
                items={items}
                value={selectedKeys}
                onValueChange={handleValueChange}
                defaultExpandedValues={expandedParentKeys}
                emptyLabel="No matching groups"
                onDeselect={() => onChange({ coaTemplateGroupKeys: undefined })}
            />
        </div>
    );
};
